import { yupResolver } from '@hookform/resolvers/yup';
import { Range } from '@thurstech/commons/dist/entity/common';
import { Listing, ListingType, SportsListing } from '@thurstech/commons/dist/entity/listing';
import { TimeBookingPackage, TimeBookingVenue, VenueType } from '@thurstech/commons/dist/entity/venue';
import { PresignedPostUploadType } from '@thurstech/commons/dist/models/common';
import { ImageToString } from '@thurstech/commons/dist/utils/types';
import { toMinutes } from '@thurstech/commons/dist/utils/util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { DeepPartial, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import AddServiceModal from './AddService';
import Button from '../../../components/button/Button';
import Input from '../../../components/input/Input';
import InputDate from '../../../components/input/InputDate';
import InputFile, { uploadLocalFiles } from '../../../components/input/InputFile';
import InputSelect from '../../../components/input/InputSelect';
import InputTextArea from '../../../components/input/InputTextArea';
import Loading from '../../../components/loading/Loading';
import { useAppDispatch, useAppSelector } from '../../../hooks/store';
import { isThunkLoading } from '../../../redux/common';
import {
  addListingThunk,
  addVenueThunk,
  deletePackageThunk,
  getListingThunk,
  getVenueThunk,
  updateListingThunk,
  updateVenueThunk
} from '../../../redux/reducer/listingSlice';
import { cityOptions, withErrorHandler } from '../../../utils/utils';
import {
  AddProductsFormData,
  addProductSchemas
} from '../../../validation/operations/addOrEditProduct/timeBooking/addProductSchemas';

dayjs.extend(utc);

type FormPackage = AddProductsFormData['packages'];
const AddTimeProducts: React.FC = () => {
  const [displayPackageModal, setDisplayPackageModal] = useState<boolean>(false);
  const [packageModalIndex, setPackageModalIndex] = useState<number>(0);
  const [packagesPreviousState, setPackagePreviousState] = useState<FormPackage>([]);
  const navigate = useNavigate();

  const { listingId, venueId } = useParams();
  const listing = useAppSelector(state => state.listing.listingMap[listingId ?? '']) as SportsListing;
  const venue = useAppSelector(state => state.listing.venueMap[venueId ?? '']) as TimeBookingVenue;
  const isLoading = useAppSelector(state => state.listing.isLoading);
  const error = useAppSelector(state => state.listing.error);
  const dispatch = useAppDispatch();

  let values: DeepPartial<AddProductsFormData> = { openSlots: [{}] };
  useEffect(() => {
    if (listingId && !listing) {
      dispatch(getListingThunk(listingId));
    }
  }, [listingId]);

  useEffect(() => {
    if (listingId && venueId && !venue) {
      dispatch(getVenueThunk({ listingId, venueId }));
    }
  }, [listingId, venueId]);

  if (listing) {
    values = {
      ...values,
      title: listing.title,
      images: listing.images,
      ageGroup: listing.ageGroup
    };
  }
  if (venue) {
    values = {
      ...values,
      shortAddress: venue.title,
      phoneNumber: venue.phoneNumber,
      city: venue.city,
      address: venue.address,
      location: venue.location,
      openDate: venue.openDate,
      openSlots: venue.openHours.default.map(openHour => ({
        from: dayjs.utc(venue.openDate.from).startOf('day').set('minutes', openHour.from).toDate(),
        to: dayjs.utc(venue.openDate.from).startOf('day').set('minutes', openHour.to).toDate()
      })),
      packages: venue.packages as FormPackage
    };
  }
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    setValue,
    watch
  } = useForm<AddProductsFormData>({
    resolver: yupResolver(addProductSchemas),
    defaultValues: values
  });

  if (listingId) {
    if (isLoading[getListingThunk.pending.toString()] || isLoading[getVenueThunk.pending.toString()]) {
      return <Loading />;
    } else if (error[getListingThunk.pending.toString()] || error[getVenueThunk.pending.toString()]) {
      navigate('/not-found');
      return <div />;
    }
  }

  const commonProps = {
    register,
    errors,
    required: true,
    topLabel: true
  };

  const onSave = async (data: AddProductsFormData) => {
    withErrorHandler(async () => {
      const listingImages = await uploadLocalFiles(
        PresignedPostUploadType.PROP_LISTING_IMAGES,
        ...data.images
      );
      setValue(
        'images',
        listingImages.map(image => ({ loc: image }))
      );
      const listing: ImageToString<Omit<Listing, 'propId'>> = {
        listingType: ListingType.SPORTS,
        images: listingImages,
        ..._.pick(data, ['title', 'ageGroup'])
      };
      const venue: Omit<TimeBookingVenue, 'propId' | 'listingId'> = {
        venueType: VenueType.TIME_BOOKING,
        title: data.shortAddress,
        packages: data.packages as TimeBookingPackage[],
        ..._.pick(data, ['phoneNumber', 'city', 'address', 'location', 'openDate']),
        openHours: {
          default: data.openSlots.map((slot: Range<Date>) => ({
            from: toMinutes(slot.from),
            to: toMinutes(slot.to)
          }))
        }
      };
      let listingIdVal = listingId;
      if (listingId) {
        await dispatch(updateListingThunk({ listingId, listing })).unwrap();
      } else {
        const { id: listingId } = await dispatch(addListingThunk(listing)).unwrap();
        listingIdVal = listingId;
        navigate(listingId);
      }
      if (venueId) {
        await dispatch(updateVenueThunk({ listingId: listingIdVal!, venueId, venue })).unwrap();
      } else {
        const { id } = await dispatch(addVenueThunk({ listingId: listingIdVal!, venue })).unwrap();
        navigate(`/product/${listingIdVal}/venue/${id}`);
      }
    }, 'Successfully saved Product Details');
  };

  const openSlots = watch('openSlots');
  const lastOpenSlot = dayjs(openSlots.at(-1)?.to).toDate() ?? dayjs().startOf('day').toDate();
  const openSlotsError = _.get(errors, 'openSlots')?.message as string;
  const packages = watch('packages') ?? [];
  const handlePackageModelOpen = (index: number) => {
    setPackagePreviousState(_.cloneDeep(packages));
    setPackageModalIndex(index);
    setDisplayPackageModal(true);
  };

  console.log('packages', packages);
  const packagesError = _.get(errors, 'packages')?.message as string;
  return (
    <form onSubmit={handleSubmit(onSave)}>
      <div className="my-6">
        <h1 className="text-2xl font-bold">Add Sports Product</h1>
      </div>
      <div className="flex space-x-8">
        <div className="grid grid-cols-2 gap-8">
          <div className="flex flex-col gap-4">
            <Input
              name="title"
              label="Title"
              type="text"
              placeholder="Enter your workshop title"
              {...commonProps}
            />
            <Input
              name="phoneNumber"
              label="Phone Number"
              type="text"
              placeholder="Enter your Phone number"
              {...commonProps}
            />
            <InputSelect
              {...commonProps}
              label="City"
              placeholder="Select City from list"
              name="city"
              options={cityOptions}
            />
            <Input
              name="location"
              label="Venue Location"
              type="text"
              placeholder="Paste your venue’s google map link"
              {...commonProps}
            />
            <InputTextArea
              label="Venue Address"
              name="address"
              placeholder="Enter your venue address"
              {...commonProps}
            />
            <Input
              label="Short Venue Address"
              name="shortAddress"
              type="text"
              placeholder="Enter your short venue address"
              {...commonProps}
            />

            <div className="flex gap-2">
              <Input
                name="ageGroup.from"
                type="number"
                className=" bg-[#f6f6f6]  text-center border border-white"
                placeholder="From"
                label="Age From"
                {...commonProps}
              />
              <Input
                name="ageGroup.to"
                type="number"
                className=" bg-[#f6f6f6]  text-center"
                placeholder="To"
                label="Age To"
                {...commonProps}
              />
            </div>
            <div className="mb-8 w-[418px] h-[104px]">
              <div className="my-8 space-y-4">
                <h2 className="block font-bold text-xl">Add Services</h2>
                <div className="bg-white rounded grid grid-cols-3 gap-4">
                  <button
                    onClick={() => handlePackageModelOpen(packages.length)}
                    className="bg-[#f6f6f6] p-4 text-center rounded cursor-pointer flex flex-col items-center justify-center"
                    type="button"
                  >
                    <i className="fas fa-plus text-[#5a5a5a]"></i>
                    <p className="font-medium text-[#9b9b9b]">Click here to add Package</p>
                  </button>
                  {packages.map((packageDetail, index) => (
                    <div key={index} className=" bg-[#f6f6f6] p-4 rounded relative">
                      <p className="text-[15px] font-[600]">{packageDetail.title}</p>
                      <p className="text-[16px]  font-[600] mt-2">₹ {packageDetail.price}</p>
                      <p className="text-[14px] font-[600] mt-2 flex justify-between">
                        Courts: <span>{packageDetail.resources.details!.length}</span>
                      </p>
                      <button
                        className="text-black text-[13px] underline mt-3"
                        type="button"
                        onClick={() => handlePackageModelOpen(index)}
                      >
                        Click here to edit
                      </button>
                      <button
                        className="absolute top-0 right-0 text-black mt-1 mr-1 rounded-full w-4 h-4 flex items-center justify-center"
                        type="button"
                        onClick={() => {
                          withErrorHandler(async () => {
                            if (packageDetail.id && listingId && venueId) {
                              await dispatch(
                                deletePackageThunk({ listingId, venueId, packageId: packageDetail.id })
                              ).unwrap();
                            }
                            setValue(
                              'packages',
                              packages.filter((_, i) => i !== index)
                            );
                          }, `Successfully removed ${packageDetail.title}`);
                        }}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
                {packagesError && <p className="text-red-600 text-sm font-semibold mt-1">{packagesError}</p>}
              </div>
              <AddServiceModal
                isOpen={displayPackageModal}
                resetState={() => {
                  console.log('previous state', packagesPreviousState);
                  setValue('packages', packagesPreviousState);
                }}
                onClose={() => setDisplayPackageModal(false)}
                index={packageModalIndex}
                listingId={listingId!}
                venueId={venueId}
                {...{ register, errors, trigger, setValue, watch }}
              />
            </div>
          </div>
          <div className="flex flex-col ">
            <div className="flex flex-col items-start ">
              <InputFile
                name="images"
                label="Images"
                required
                multiple
                maxFiles={4}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                addtionalText={
                  <p className="text-sm font-medium mt-1 text-gray-300">(Click here to add photo)</p>
                }
                accept="image/*"
              />
            </div>
            <div className="flex gap-2">
              <InputDate
                className="w-[95px] h-[39px]"
                name="openDate.from"
                label="Date From"
                placeholder="From"
                setValue={setValue}
                watch={watch}
                errors={errors}
                required
              />
              <InputDate
                className="w-[95px] h-[39px]"
                name="openDate.to"
                label="Date Till"
                placeholder="To"
                setValue={setValue}
                watch={watch}
                errors={errors}
                required
              />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className=" font-medium block mb-1">
                  Open Timings <span className="text-red-600">*</span>
                </span>
                <button
                  onClick={async () => {
                    const isValid = await trigger('openSlots');
                    if (isValid) {
                      setValue('openSlots', [
                        ...openSlots,
                        { from: lastOpenSlot, to: dayjs().endOf('day').set('seconds', 0).toDate() }
                      ]);
                    }
                  }}
                  type="button"
                  className="border border-black bg-white rounded-md px-2"
                >
                  +
                </button>
              </div>
              {openSlotsError && <p className="text-red-600 text-sm font-semibold">{openSlotsError}</p>}
            </div>
            {openSlots.map((_, index) => (
              <div className="flex" key={index}>
                <InputDate
                  className="w-[95px] h-[39px]"
                  name={`openSlots.${index}.from`}
                  label=""
                  placeholder="From"
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                  defaultDate={watch('openDate.from')}
                  isTimeSelect
                />
                <span className="mx-2 text-black">to</span>
                <InputDate
                  className="w-[95px] h-[39px]"
                  name={`openSlots.${index}.to`}
                  label=""
                  placeholder="To"
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                  defaultDate={watch('openDate.from')}
                  isTimeSelect
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-end space-y-4 ml-2 mt-6 font-medium">
          <Button
            className="bg-emerald-400 text-white px-2 py-2 rounded w-48"
            type="submit"
            label="Save Changes"
            isLoading={isThunkLoading(
              isLoading,
              addListingThunk,
              updateListingThunk,
              addVenueThunk,
              updateVenueThunk,
              deletePackageThunk
            )}
          />
          {/* <button className="bg-[#f8f8f8] text-[#ff0000] px-2 py-2 rounded w-56" type="button">
            Pause bookings
          </button> */}
        </div>
      </div>
    </form>
  );
};

export default AddTimeProducts;
